// src/services/authService.js
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('kapta_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Clear storage if token is invalid
      localStorage.removeItem('kapta_token');
      localStorage.removeItem('kapta_user');

      // Don't redirect - let the component handle it
      console.log('Session expired or invalid token');
    }
    return Promise.reject(error);
  }
);

export const authService = {
  // Register/Create Account
  async register(userData) {
    try {
      console.log('Registering user:', { ...userData, password: '***' });
      const response = await api.post('/api/v1/auth/register', userData);
      console.log('Register response:', response.data);

      if (response.data.success) {
        localStorage.setItem('kapta_token', response.data.token);
        localStorage.setItem('kapta_user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Register error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed';
      throw new Error(errorMessage);
    }
  },

  // Sign In
  async login(credentials) {
    try {
      console.log('Logging in:', { ...credentials, password: '***' });
      const response = await api.post('/api/v1/auth/login', credentials);
      console.log('Login response:', response.data);

      if (response.data.success) {
        localStorage.setItem('kapta_token', response.data.token);
        localStorage.setItem('kapta_user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Login error:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.message || error.message || 'Login failed';
      throw new Error(errorMessage);
    }
  },

  // ✅ Google Sign-In / Sign-Up
  // credential = Google ID token (JWT) returned by Google Identity Services
  async googleLogin(credential) {
    try {
      console.log('Google login with credential');
      const response = await api.post('/api/v1/auth/google', { credential });
      console.log('Google login response:', response.data);

      if (response.data.success) {
        localStorage.setItem('kapta_token', response.data.token);
        localStorage.setItem('kapta_user', JSON.stringify(response.data.user));
      }
      return response.data;
    } catch (error) {
      console.error('Google login error:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.message || error.message || 'Google sign-in failed';
      throw new Error(errorMessage);
    }
  },

  // Forgot Password
  async forgotPassword(email) {
    try {
      console.log('Forgot password for:', email);
      const response = await api.post('/api/v1/auth/forgot-password', { email });
      console.log('Forgot password response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Forgot password error:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to send reset email';
      throw new Error(errorMessage);
    }
  },

  // Reset Password
  async resetPassword(token, newPassword) {
    try {
      console.log('Resetting password');
      const response = await api.post('/api/v1/auth/reset-password', {
        token,
        newPassword,
      });
      console.log('Reset password response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Reset password error:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.message || error.message || 'Failed to reset password';
      throw new Error(errorMessage);
    }
  },

  // Get current user profile
  async getCurrentUser() {
    try {
      const token = localStorage.getItem('kapta_token');
      if (!token) {
        console.log('No token found');
        return null;
      }

      console.log('Getting current user with token');
      const response = await api.get('/api/v1/auth/me');
      console.log('Get current user response:', response.data);

      if (response.data.success) {
        localStorage.setItem('kapta_user', JSON.stringify(response.data.user));
        return response.data.user;
      }
      return null;
    } catch (error) {
      console.error('Get user error:', error.response?.data || error.message);
      return null;
    }
  },

  // Verify reset token
  async verifyResetToken(token) {
    try {
      console.log('Verifying reset token:', token.substring(0, 20) + '...');
      const response = await api.post('/api/v1/auth/verify-reset-token', { token });
      console.log('Verify token response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Verify token error:', error.response?.data || error.message);
      const errorMessage =
        error.response?.data?.message || error.message || 'Invalid reset token';
      throw new Error(errorMessage);
    }
  },

  // Logout
  logout() {
    console.log('Logging out');
    localStorage.removeItem('kapta_token');
    localStorage.removeItem('kapta_user');
  },

  // Get stored user
  getStoredUser() {
    const userStr = localStorage.getItem('kapta_user');
    const user = userStr ? JSON.parse(userStr) : null;
    console.log('Stored user:', user);
    return user;
  },

  // Check if user is authenticated
  isAuthenticated() {
    const hasToken = !!localStorage.getItem('kapta_token');
    console.log('Is authenticated:', hasToken);
    return hasToken;
  },

  // Get token
  getToken() {
    const token = localStorage.getItem('kapta_token');
    console.log('Token exists:', !!token);
    return token;
  },
};